import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Radio, RadioGroup } from '@thanx/uikit/radio'
import { useStyletron } from '@thanx/uikit/theme'

import { alert } from 'actions/flash'
import { useUpdateMerchantMutation } from 'api/merchants'
import ConfirmModal from 'components/ConfirmModal'
import Section from 'components/Section'
import palette from 'constants/palette'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import CmsPage from 'scenes/Cms/components/CmsPage'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import { selectCurrentMerchant } from 'selectors/merchant'
import { hasToastLocation } from './hasToastLocation'

const t = buildTranslate('cms.settings.instore_enrollment')

const InstoreEnrollment: React.FC<{ showTitle?: boolean }> = ({
  showTitle = true,
}) => {
  const [css] = useStyletron()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useDispatch()
  const merchant = useSelector(selectCurrentMerchant)

  const [updateMerchant, { isLoading: isSubmitting }] =
    useUpdateMerchantMutation()

  const [enrollmentSetting, setEnrollmentSetting] = useState<string>(
    merchant?.pos_enrollment_setting || 'never'
  )

  const isToastMerchant = hasToastLocation(merchant)

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(messageKey),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    if (merchant?.pos_enrollment_setting) {
      setEnrollmentSetting(merchant.pos_enrollment_setting)
    }
  }, [merchant?.pos_enrollment_setting])

  const handlePublish = async () => {
    try {
      await updateMerchant({
        data: {
          pos_enrollment_setting: enrollmentSetting as
            | 'never'
            | 'on_request'
            | 'always',
        },
      }).unwrap()
      displayAlert('success', 'submit_success')
    } catch (error) {
      displayAlert('danger', 'submit_error')
    }

    setIsModalOpen(false)
  }

  const hasChanges = enrollmentSetting !== merchant?.pos_enrollment_setting

  return (
    <>
      <CmsPage
        showTitle={showTitle}
        title={t('title')}
        description={`${t('description')} <p><a href="${t(
          'learn_more_url'
        )}" target="_blank" rel="noopener noreferrer" class="text-decoration-none">${t(
          'learn_more'
        )}</a></p>`}
      >
        <Section className="p-m mb-m">
          <RadioGroup
            value={enrollmentSetting}
            onChange={value => setEnrollmentSetting(value)}
          >
            <Radio value="never">
              <span className={css({ color: '#36424E' })}>
                {t('options.never.title')}
              </span>
            </Radio>
            <p
              className={`body-text-4 ml-l mt-xxs ${css({ color: '#6A747F' })}`}
            >
              {t('options.never.description')}
            </p>

            <Radio value="on_request" className="mt-s">
              <span className={css({ color: '#36424E' })}>
                {t('options.on_request.title')}
              </span>
            </Radio>
            <p
              className={`body-text-4 ml-l mt-xxs ${css({ color: '#6A747F' })}`}
            >
              {t('options.on_request.description')}{' '}
              <a
                href={t('learn_more_url')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
              >
                {t('options.on_request.description_link')}
              </a>
            </p>
            {isToastMerchant && (
              <p
                className={`body-text-4 ml-l mt-xxs ${css({
                  color: palette.grey70,
                })}`}
              >
                <strong>{t('options.on_request.toast_note.label')}</strong>{' '}
                {t('options.on_request.toast_note.description')}{' '}
                <a
                  href={t('options.on_request.toast_note.link_url')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  {t('options.on_request.toast_note.link')}
                  <span className="sr-only"> ({t('opens_in_new_tab')})</span>
                </a>
              </p>
            )}

            <Radio value="always" className="mt-s">
              <span className={css({ color: '#36424E' })}>
                {t('options.always.title')}
              </span>
            </Radio>
            <p
              className={`body-text-4 ml-l mt-xxs ${css({ color: '#6A747F' })}`}
            >
              {t('options.always.description')}{' '}
              <a
                href={t('learn_more_url')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
              >
                {t('options.always.description_link')}
              </a>{' '}
              {t('options.always.description_suffix')}
            </p>
          </RadioGroup>

          <p className={`body-text-5 mt-s ${css({ color: '#6A747F' })}`}>
            *{t('cost_note')}
          </p>
        </Section>

        <PublishBanner
          buttonText={t('publish')}
          isLoading={isSubmitting}
          disabled={!hasChanges}
          buttonType="button"
          onClick={() => setIsModalOpen(true)}
        />
      </CmsPage>
      <ConfirmModal
        title={t('modal.publish_changes')}
        description={t('modal.you_are_publishing')}
        confirmText={t('modal.publish')}
        isOpen={isModalOpen}
        isLoading={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => handlePublish()}
      />
    </>
  )
}

export default InstoreEnrollment
